import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSave } from "react-icons/fa";
import api from '../../api/manage_database/ConverterReq';
import BackButton from "../../components/BackButton";
import SelectConverterType from "../../components/SelectConverterType";
import InputTrackAmount from "../../components/InputTrackAmount";
import { toast } from "sonner";
import ExistingDeviceChoices from "../../components/ExistingDeviceChoices";

const existingConverterFields = [
    { key: 'type', label: 'Select a converter type' },
    { key: 'track_amount', label: 'Select a track amount' },
];

const ConverterNew = ({ embedded = false, onCreated, onSavingChange }) => {
    
    const [converterTypes, setConverterTypes] = useState([]);
    const [selectedConverterType, setSelectedConverterType] = useState('');
    const [trackAmount, setTrackAmount] = useState(2);
    const [selectedExistingConverter, setSelectedExistingConverter] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);
    
    const navigate = useNavigate();

    useEffect(() => {
        const fetchConverterTypes = async () => {
            try {
                const response = await api.get('types', {withCredentials: true});
                if (response.data.success) {
                    setConverterTypes(response.data.payload.types);
                } else {
                    toast.error('Not got data from database');
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchConverterTypes();
    }, []);

    const handleInputTrackAmount = (e) => {
        setSelectedExistingConverter('');
        setSaveError('');
        if (e.target.value > 4) {
            setTrackAmount(4);    
        } else if (e.target.value < 1) {
            setTrackAmount(1);
        } else {
            setTrackAmount(e.target.value);
        }        
    }

    const handleAdd = async () => {
        if (savingRef.current) return;
        setSaveError('');
        if (selectedConverterType === '') {
            toast.error('Set correct converter Type!')
            return
        }

        savingRef.current = true;
        setSaving(true);
        onSavingChange?.(true);
        try {
            const response = await api.post(
                '',
                {
                    type: selectedConverterType,
                    track_amount: trackAmount,
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                toast.success(response.data.message);
                if (embedded) {
                    onCreated?.(response.data.payload[0]);
                    return;
                }
                navigate(
                    `../record/${response.data.payload[0].id}`,
                    {
                        state: {
                            addRecordMessage: 'Added record to database!',
                            anotherTypeOfPage: true
                        }
                    }
                )
            } else {
                const message = response.data.message || 'Could not add the converter. Please try again.';
                setSaveError(message);
                toast.error(message);
            }
        } catch (err) {
            const message = err.response?.data?.message || 'Could not add the converter. Please try again.';
            setSaveError(message);
            toast.error(message);
        } finally {
            savingRef.current = false;
            setSaving(false);
            onSavingChange?.(false);
        }
    };

    return (
        <div className={`converter-new-container${embedded ? ' converter-new-inline' : ''}`}>
            {embedded ? <h3 className='converter-new-title'>Add Converter</h3> : <h2 className='converter-new-title'>Converter</h2>}
            <div className='converter-new-card'>
                <div className='converter-new-label-select-wrapper'>
                    <label
                        htmlFor='converter-type-select'
                        className='bold-label'
                    >
                        Type:
                    </label>
                    <SelectConverterType
                        ConverterTypes={converterTypes}
                        selectedConverterType={selectedConverterType}
                        setSelectedConverterType={(value) => {
                            setSelectedConverterType(value);
                            setSelectedExistingConverter('');
                            setSaveError('');
                        }}
                    />
                </div>
                <div className='converter-new-label-select-wrapper'>
                    <label
                        htmlFor='track-amount-input'
                        className='bold-label'
                    >
                        Track Amount:
                    </label>
                    <InputTrackAmount
                        trackAmount={trackAmount}
                        handleInputTrackAmount={handleInputTrackAmount}
                    />
                </div>
                {saveError && <p className="device-new-inline-error" role="alert">{saveError}</p>}
                {embedded && (
                    <ExistingDeviceChoices
                        api={api}
                        kind="converter"
                        fields={existingConverterFields}
                        selectedId={selectedExistingConverter}
                        disabled={saving}
                        onSelect={(converter) => {
                            setSelectedExistingConverter(converter?.id || '');
                            setSaveError('');
                            if (converter) {
                                setSelectedConverterType(converter.type);
                                setTrackAmount(converter.track_amount);
                            }
                        }}
                    />
                )}
                <button type="button" className='converter-new-button' onClick={handleAdd} disabled={saving}>
                    {saving ? 'Saving...' : embedded ? 'Add converter' : 'Add'} &nbsp; <FaSave/>
                </button>
            </div>
            {!embedded && <BackButton navigateTo='/manage-database'/>}
        </div>
    );
        
};

export default ConverterNew;
